import {
  Box, Button, Text, Flex, Input,
  TableContainer, Table, Thead, Tr, Th, Td, Tbody,
  Select, Badge, Spinner,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { api } from "../../../services/api";
import { Logistics } from "../Logistics";
import { OverdueOrdersAlert } from "../OverdueOrdersAlert";

const GREEN  = "#004a33";
const ORANGE = "#fc8f04";
const PAGE_SIZE = 25;

const MONTHS = [
  { v: "01", l: "Janeiro" }, { v: "02", l: "Fevereiro" }, { v: "03", l: "Março" },
  { v: "04", l: "Abril" },   { v: "05", l: "Maio" },     { v: "06", l: "Junho" },
  { v: "07", l: "Julho" },   { v: "08", l: "Agosto" },   { v: "09", l: "Setembro" },
  { v: "10", l: "Outubro" }, { v: "11", l: "Novembro" }, { v: "12", l: "Dezembro" },
];

const DELIVERY_STATUS = {
  pending:          { label: "Aguardando",    color: "orange" },
  out_for_delivery: { label: "Em entrega",    color: "blue"   },
  delivered:        { label: "Entregue",      color: "green"  },
  collected:        { label: "Retirado",      color: "green"  },
  cancelled:        { label: "Cancelado",     color: "red"    },
};

const inputProps = {
  borderRadius: "8px",
  fontSize: "13px",
  bg: "white",
  border: "1px solid rgba(0,74,51,.2)",
  _focus: { borderColor: GREEN, boxShadow: `0 0 0 1px ${GREEN}` },
};

// Operational order management — split out from "Relatório" (now
// analytics-only) so admins can search/filter/act on individual orders
// without the stat cards and period filter competing for the same screen.
export function OrdersCard() {
  const [allOrders, setAllOrders]     = useState([]);
  const [allUsers, setAllUsers]       = useState([]);
  const [batches, setBatches]         = useState([]);
  const [products, setProducts]       = useState([]);
  const [payments, setPayments]       = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1).padStart(2, "0"));
  const [selectedYear, setSelectedYear]   = useState(String(new Date().getFullYear()));
  const [search, setSearch]           = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("");
  const [page, setPage]               = useState(1);
  const [loading, setLoading]         = useState(false);

  const minYear = 2023;
  const maxYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);

  async function fetchOrders(year, month) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (year)  params.append("year", year);
      if (month) params.append("month", month);
      const response = await api.get(`/api/v1/batch_orders${params.size ? `?${params}` : ""}`);
      setAllOrders(response.data.batch_orders || []);
      setBatches(response.data.batches || []);
      setProducts(response.data.products || []);
      setPayments(response.data.payments || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchUsers() {
    try {
      const response = await api.get("/api/v1/users");
      setAllUsers(response.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }

  useEffect(() => {
    fetchOrders(parseInt(selectedYear, 10), parseInt(selectedMonth, 10));
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateOrderStatus = (orderId, newStatus) => {
    setAllOrders(prev => prev.map(o => o.id === orderId ? { ...o, delivery_status: newStatus } : o));
  };

  const handlePaymentMarkedPaid = (updatedBatchOrders) => {
    setAllOrders(prev => prev.map(o => {
      const updated = updatedBatchOrders?.find(u => u.id === o.id);
      return updated ? { ...o, paid: updated.paid, status: updated.status } : o;
    }));
  };

  function getPaymentMethod(order) {
    const payment = payments.find(p => p.id === order.payment_id);
    return payment?.payment_method === "pix" ? "pix" : "card";
  }

  function getProductName(order) {
    const batch = batches.find(b => b.id === order.batch_id);
    return products.find(p => p.id === batch?.product_id)?.name || "—";
  }

  function getClientName(order) {
    return allUsers.find(u => u.id === order.user_id)?.full_name || "—";
  }

  function getClientEmail(order) {
    return allUsers.find(u => u.id === order.user_id)?.email || "";
  }

  const filteredOrders = useMemo(() => {
    let list = allOrders;

    if (statusFilter === "paid") list = list.filter(o => o.paid);
    else if (statusFilter === "open") list = list.filter(o => !o.paid && o.delivery_status !== "cancelled");
    else if (statusFilter === "cancelled") list = list.filter(o => o.delivery_status === "cancelled");

    if (paymentMethodFilter) list = list.filter(o => getPaymentMethod(o) === paymentMethodFilter);

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter(o =>
        getClientName(o).toLowerCase().includes(q) ||
        getClientEmail(o).toLowerCase().includes(q) ||
        getProductName(o).toLowerCase().includes(q) ||
        String(o.id).includes(q)
      );
    }

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allOrders, statusFilter, paymentMethodFilter, search, allUsers, batches, products, payments]);

  useEffect(() => { setPage(1); }, [search, statusFilter, paymentMethodFilter, selectedMonth, selectedYear]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedOrders = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredOrders.slice(start, start + PAGE_SIZE);
  }, [filteredOrders, currentPage]);

  function getPaymentDate(order) {
    const payment = payments.find(p => p.id === order.payment_id);
    if (!payment?.invoice_paid_at) return null;
    const d = new Date(payment.invoice_paid_at);
    if (isNaN(d)) return null;
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" }) +
           " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }

  const periodLabel = selectedMonth && selectedYear
    ? `${MONTHS.find(m => m.v === selectedMonth)?.l} ${selectedYear}`
    : "Todos os períodos";

  return (
    <Box>
      {/* Header */}
      <Flex align="center" justify="space-between" mb="20px" wrap="wrap" gap="10px">
        <Box>
          <Text fontSize="11px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.1em" mb="4px">
            Gestão de pedidos
          </Text>
          <Text fontSize="24px" fontWeight="900" color={GREEN}>Pedidos</Text>
        </Box>
      </Flex>

      <OverdueOrdersAlert />

      {/* Filters */}
      <Box bg="white" borderRadius="14px" p="16px 20px" mb="18px" border="1px solid rgba(0,74,51,.08)" boxShadow="0 2px 10px rgba(0,74,51,.05)">
        <Flex gap="10px" align="flex-end" flexWrap="wrap" mb="12px">
          <Box flex="1" minW="120px">
            <Text fontSize="11px" color="gray.500" mb="4px" fontWeight="600">Mês</Text>
            <Select size="sm" borderRadius="8px" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
              {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </Select>
          </Box>
          <Box flex="1" minW="100px">
            <Text fontSize="11px" color="gray.500" mb="4px" fontWeight="600">Ano</Text>
            <Select size="sm" borderRadius="8px" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </Select>
          </Box>
          <Button
            size="sm" borderRadius="8px" h="32px" px="16px"
            bg={GREEN} color={ORANGE} fontWeight="800" fontSize="12px"
            _hover={{ opacity: 0.88 }}
            onClick={() => fetchOrders(parseInt(selectedYear, 10), parseInt(selectedMonth, 10))}
            isLoading={loading}
          >
            Buscar período
          </Button>
          <Text fontSize="11px" color="gray.400" ml="4px">{periodLabel}</Text>
        </Flex>

        <Flex gap="10px" align="flex-end" flexWrap="wrap">
          <Box position="relative" flex="2" minW="220px">
            <Text fontSize="11px" color="gray.500" mb="4px" fontWeight="600">Buscar</Text>
            <Box position="absolute" left="10px" top="32px" color="gray.400" pointerEvents="none">
              <FiSearch size={13} />
            </Box>
            <Input
              pl="30px" size="sm"
              placeholder="Cliente, e-mail, produto ou #pedido…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              {...inputProps}
            />
          </Box>
          <Box flex="1" minW="150px">
            <Text fontSize="11px" color="gray.500" mb="4px" fontWeight="600">Status</Text>
            <Select size="sm" borderRadius="8px" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">Todos</option>
              <option value="paid">Pagos</option>
              <option value="open">Em aberto</option>
              <option value="cancelled">Cancelados</option>
            </Select>
          </Box>
          <Box flex="1" minW="150px">
            <Text fontSize="11px" color="gray.500" mb="4px" fontWeight="600">Pagamento</Text>
            <Select size="sm" borderRadius="8px" value={paymentMethodFilter} onChange={e => setPaymentMethodFilter(e.target.value)}>
              <option value="">Todos</option>
              <option value="pix">PIX</option>
              <option value="card">Cartão</option>
            </Select>
          </Box>
        </Flex>
      </Box>

      <Text fontSize="12px" color="gray.400" mb="10px">
        {filteredOrders.length} de {allOrders.length} pedido{allOrders.length !== 1 ? "s" : ""} no período
      </Text>

      {/* Table */}
      {loading ? (
        <Flex justify="center" py="60px">
          <Spinner size="xl" color={GREEN} thickness="3px" />
        </Flex>
      ) : filteredOrders.length === 0 ? (
        <Box bg="white" borderRadius="14px" p="48px" textAlign="center"
          border="1px solid rgba(0,74,51,.08)">
          <Text fontSize="16px" fontWeight="700" color="gray.400">Nenhum pedido encontrado</Text>
          <Text fontSize="13px" color="gray.300" mt="6px">Ajuste os filtros, a busca ou o período selecionado</Text>
        </Box>
      ) : (
        <Box bg="white" borderRadius="16px" border="1px solid rgba(0,74,51,.08)"
          boxShadow="0 2px 12px rgba(0,74,51,.05)" overflow="hidden">
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead bg="rgba(0,74,51,.04)">
                <Tr>
                  <Th color={GREEN} fontSize="11px" py="12px">#</Th>
                  <Th color={GREEN} fontSize="11px">Cliente</Th>
                  <Th color={GREEN} fontSize="11px">Produto</Th>
                  <Th color={GREEN} fontSize="11px">Qtd</Th>
                  <Th color={GREEN} fontSize="11px">Valor</Th>
                  <Th color={GREEN} fontSize="11px">Pagamento</Th>
                  <Th color={GREEN} fontSize="11px">Tipo</Th>
                  <Th color={GREEN} fontSize="11px">Entrega</Th>
                  <Th color={GREEN} fontSize="11px">Ação</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pagedOrders.map((order) => {
                  const ds = DELIVERY_STATUS[order.delivery_status];
                  const payDate = getPaymentDate(order);
                  const isCancelled = order.delivery_status === "cancelled";

                  return (
                    <Tr
                      key={order.id}
                      bg={isCancelled ? "gray.50" : order.paid ? "rgba(0,74,51,.025)" : "rgba(252,143,4,.04)"}
                      opacity={isCancelled ? 0.7 : 1}
                      _hover={{ bg: isCancelled ? "gray.100" : "rgba(0,74,51,.05)" }}
                      transition="background 0.1s"
                    >
                      <Td py="10px">
                        <Text fontSize="12px" fontWeight="700" color={GREEN}>#{order.id}</Text>
                        <Text fontSize="10px" color="gray.400">pag.#{order.payment_id}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="12px" fontWeight="600" noOfLines={1}>{getClientName(order)}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="12px" noOfLines={1} maxW="140px">{getProductName(order)}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="12px">{order.quantity} {batches.find(b => b.id === order.batch_id)?.unity_of_measurement || ""}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="13px" fontWeight="800" color={GREEN}>
                          {order.amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                        </Text>
                      </Td>
                      <Td>
                        {order.paid ? (
                          <Box>
                            <Badge colorScheme="green" fontSize="10px" borderRadius="6px" px="6px">
                              {getPaymentMethod(order) === "pix" ? "PIX" : "Cartão"}
                            </Badge>
                            {payDate && (
                              <Text fontSize="10px" color="gray.400" mt="2px">{payDate}</Text>
                            )}
                          </Box>
                        ) : (
                          <Badge colorScheme="orange" fontSize="10px" borderRadius="6px" px="6px">
                            Em aberto
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <Badge
                          fontSize="10px" borderRadius="6px" px="6px"
                          colorScheme={order.delivery_method === "collect" ? "teal" : "blue"}
                        >
                          {order.delivery_method === "collect" ? "Retirada" : "Entrega"}
                        </Badge>
                      </Td>
                      <Td>
                        {order.paid ? (
                          <Badge
                            colorScheme={ds?.color || "gray"}
                            fontSize="10px" borderRadius="6px" px="6px"
                          >
                            {ds?.label || order.delivery_status}
                          </Badge>
                        ) : (
                          <Badge colorScheme="gray" fontSize="10px" borderRadius="6px" px="6px">
                            Aguard. pagamento
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <Logistics
                          orderId={order.id}
                          updateOrderStatus={updateOrderStatus}
                          onPaymentMarkedPaid={handlePaymentMarkedPaid}
                          deliveryStatus={order.delivery_status}
                          isPaid={order.paid}
                        />
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
          <Flex
            align="center" justify="space-between" px="20px" py="10px"
            borderTop="1px solid rgba(0,74,51,.06)" wrap="wrap" gap="8px"
          >
            <Text fontSize="12px" color="gray.400">
              {filteredOrders.length} pedido{filteredOrders.length !== 1 ? "s" : ""} exibido{filteredOrders.length !== 1 ? "s" : ""}
            </Text>
            {filteredOrders.length > 0 && (
              <Flex align="center" gap="10px">
                <Button
                  size="xs" variant="ghost" color={GREEN} fontWeight="700" fontSize="11px"
                  isDisabled={currentPage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Text fontSize="12px" color="gray.500" fontWeight="600">
                  Página {currentPage} de {totalPages}
                </Text>
                <Button
                  size="xs" variant="ghost" color={GREEN} fontWeight="700" fontSize="11px"
                  isDisabled={currentPage >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                >
                  Próxima
                </Button>
              </Flex>
            )}
          </Flex>
        </Box>
      )}
    </Box>
  );
}
